import { Box, styled, Tooltip } from "@mui/material";
import { ColumnsPanelTrigger, DataGridPro, DataGridProProps, Toolbar, ToolbarButton } from "@mui/x-data-grid-pro";
import { useContext, useMemo } from "react";
import { AppContext } from "../../../../../../common/contexts/app_context/app_context";
import { useColumnSelectionConfig } from "../../../../../../common/hooks/use_column_selection_config";

import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { ValoresContasMes } from "../../../../../comercial/domain/models/valores_contas_mes";

/**
 * CustomToolbar
 */

const CustomToolbar = ({ valores }: any) => {
    return (
        <div className="w-full">
            <Toolbar>
                <Tooltip title="Colunas">
                    <ColumnsPanelTrigger render={<ToolbarButton />}>
                        <ViewColumnIcon fontSize="small" />
                    </ColumnsPanelTrigger>
                </Tooltip>
            </Toolbar>
            {valores && (
                <div className="flex justify-start pe-[150px] pb-3 m-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3">
                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-blue-600">Data Inicial</span>
                            <span className="text-lg font-bold">  {new Date(valores.dataInicial).toLocaleDateString("pt-BR")}
                            </span>
                        </div>

                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-blue-700">Data Final</span>
                            <span className="text-lg font-bold">{new Date(valores.dataFinal).toLocaleDateString("pt-BR")}</span>
                        </div>

                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-red-700">Pagamentos Atrasados</span>
                            <span className="text-lg font-bold">{toBRL(valores.atrasado)}</span>
                        </div>
                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-gray-700">Valor a Pagar</span>
                            <span className="text-lg font-bold">{toBRL(valores.valorPagar)}</span>
                        </div>

                        <div className="bg-gray-200 px-4 py-3 rounded-xl shadow-sm flex flex-col text-center">
                            <span className="text-sm font-semibold text-gray-700">Valor Pago</span>
                            <span className="text-lg font-bold">{toBRL(valores.valorPago)}</span>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}

const toBRL = (value?: number) => {
    return (
        value?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) ?? "R$ 0,00"
    );
}

interface Props extends DataGridProProps {
    tableName: string;
    height?: string;
    valoresContasMes?: ValoresContasMes;
}

export const ContasPagarDataGrid = ({ tableName, valoresContasMes, disableRowSelectionExcludeModel = true, height = "80vh", ...props }: Props) => {
    const [
        columnsVisibilityModel,
        handleColumnsVisibilityModel] = useColumnSelectionConfig({
            tabela: tableName,
        });

    const mode = useContext(AppContext).theme
    const StyledDataGrid = useMemo(() => {
        return styled(DataGridPro)(() => ({
            '& .MuiDataGrid-footerContainer': {
                display: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                lineHeight: '1.2',
            },
            '& .MuiDataGrid-cell': {
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                lineHeight: '1.2',
                padding: '8px 0',
            },
            backgroundColor: mode === "dark" ? "#2C2C2C" : "#FFFFFF",
        }))
    }, [mode]);

    return (
        <Box sx={{ height: height, width: '100%' }}>

            <StyledDataGrid
                showToolbar={true}
                slots={{
                    toolbar: CustomToolbar,
                }}
                slotProps={{
                    toolbar: {
                        valores: valoresContasMes,
                    } as any,
                    columnsPanel: {
                        style: {
                            color: mode === "dark" ? '#ffffff' : "#000000",
                        }
                    },
                }}
                columnVisibilityModel={columnsVisibilityModel}
                disableColumnSorting={true}
                multipleColumnsSortingMode='always'
                disableRowSelectionExcludeModel={disableRowSelectionExcludeModel}
                onColumnVisibilityModelChange={handleColumnsVisibilityModel}
                {...props}
                disableColumnMenu={false}
                localeText={{
                    noRowsLabel: "Não existem itens para mostrar!",

                    columnsManagementSearchTitle: 'Buscar',
                    columnsManagementNoColumns: 'Não há colunas',
                    columnsManagementShowHideAllText: 'Exibir/Esconder',
                    columnsManagementReset: 'Reiniciar',
                    columnsManagementDeleteIconLabel: 'Limpar',

                    pinToLeft: 'Fixar a esquerda',
                    pinToRight: 'Fixar a direita',
                    unpin: 'Desfixar',

                    columnMenuShowColumns: 'Exibir colunas',
                    columnMenuManageColumns: 'Gerenciar colunas',

                    columnMenuHideColumn: 'Esconder coluna',
                    columnMenuUnsort: 'Desordenar',
                    columnMenuSortAsc: 'Ordenar ASC',
                    columnMenuSortDesc: 'Ordenar DESC',
                }}
            />

        </Box>
    );
}